import * as Sentry from '@sentry/vue';
import type { CaptureContext, Event, EventHint, Integration, Scope, SeverityLevel } from '@sentry/core';
import type { BrowserOptions } from '@sentry/vue';
import type { NuxtApp } from 'nuxt/app';
import type { Router } from 'vue-router';
import { type WatchStopHandle, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { extraClientTags, getPublicSentryConfig, isBenignClientEvent, parseBoolean, shouldEnableClientSentry } from '~/utils/sentry';
import { useUsersStore } from '~/stores/users';

const escapeForRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const resolveTracePropagationTargets = (): Array<string | RegExp> => {
  if (typeof window === 'undefined') {
    return [/^\//];
  }

  const originPattern = new RegExp(`^${escapeForRegex(window.location.origin)}`);
  return [/^\//, originPattern];
};

const createClientIntegrations = (
  router: Router | undefined,
  allowReplay: boolean,
  config: ReturnType<typeof getPublicSentryConfig>,
  tracePropagationTargets: Array<string | RegExp>,
): BrowserOptions['integrations'] => {
  const integrations: BrowserOptions['integrations'] = [
    Sentry.browserTracingIntegration({
      router,
      tracePropagationTargets,
    }),
  ];

  if (allowReplay) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllInputs: config.replayMaskAllInputs,
        maskAllText: config.replayMaskAllText,
        blockAllMedia: config.replayBlockAllMedia,
      }),
    );

    const maybeReplayCanvasIntegration = (Sentry as { replayCanvasIntegration?: () => Integration })
      .replayCanvasIntegration;
    if (typeof maybeReplayCanvasIntegration === 'function') {
      integrations.push(maybeReplayCanvasIntegration());
    } else if (config.debug) {
      console.debug('[Sentry] replayCanvasIntegration indisponible sur cette version du SDK.');
    }
  }

  return integrations;
};

const beforeSendFactory = (config: ReturnType<typeof getPublicSentryConfig>): NonNullable<BrowserOptions['beforeSend']> => {
  return (event: Event, hint?: EventHint) => {
    if (isBenignClientEvent(event, hint)) {
      return null;
    }

    if (config.debug && hint?.originalException) {
      console.error('[Sentry] Exception capturée', hint.originalException, { event, hint });
    }

    return event;
  };
};

const setRouteScopeMetadata = (path: string): void => {
  const scope = Sentry.getCurrentScope();
  scope.setTag('route_path', path);
  scope.setContext('route', { path });
};

const registerRouteBreadcrumbs = (nuxtApp: NuxtApp, config: ReturnType<typeof getPublicSentryConfig>): void => {
  if (!nuxtApp.$router) {
    return;
  }

  nuxtApp.$router.afterEach((to, from) => {
    if (to.fullPath === from.fullPath) {
      return;
    }

    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `${from.fullPath || 'start'} -> ${to.fullPath}`,
      level: 'info',
      type: 'navigation',
      data: {
        from: from.fullPath,
        to: to.fullPath,
      },
    });

    setRouteScopeMetadata(to.fullPath);

    if (config.captureConsole) {
      console.debug('[Sentry] Navigation breadcrumb enregistré', { from: from.fullPath, to: to.fullPath });
    }
  });
};

export default defineNuxtPlugin((nuxtApp) => {
  if (import.meta.env.TEST) {
    return;
  }

  const runtimeConfig = useRuntimeConfig();
  const config = getPublicSentryConfig(runtimeConfig);
  const sendTestError = parseBoolean(runtimeConfig.public?.sentry?.sendTestError ?? false);
  const sentryEnabled = shouldEnableClientSentry(config);

  if (!sentryEnabled && !sendTestError) {
    if (config.debug) {
      console.warn('[Sentry] Sentry client désactivé (no DSN, flag ou environnement).');
    }
    return;
  }

  if (sendTestError && !config.dsn) {
    console.warn('[Sentry] Test error flag enabled but no DSN configured; skipping capture.');
    return;
  }

  Sentry.init({
    app: [nuxtApp.vueApp],
    dsn: config.dsn,
    debug: config.debug,
    environment: config.environment,
    release: config.release,
    sendClientReports: true,
    autoSessionTracking: true,
    integrations: createClientIntegrations(
      nuxtApp.$router,
      config.allowReplay,
      config,
      resolveTracePropagationTargets(),
    ),
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeSend: beforeSendFactory(config),
  });

  const tags = extraClientTags(nuxtApp, config);
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });

  if (config.captureConsole) {
    const originalConsoleError = console.error.bind(console);
    console.error = (...args: unknown[]): void => {
      Sentry.addBreadcrumb({
        category: 'console',
        level: 'error',
        message: args.map(argument => String(argument)).join(' '),
        data: { args },
      });
      originalConsoleError(...args);
    };
  }

  nuxtApp.vueApp.mixin(
    Sentry.createTracingMixins({ trackComponents: true, timeout: 2000, hooks: ['activate', 'mount', 'update'] }),
  );
  Sentry.attachErrorHandler(nuxtApp.vueApp, {
    logErrors: config.debug,
    attachProps: true,
    trackComponents: true,
    timeout: 2000,
    hooks: ['activate', 'mount', 'update'],
  });

  registerRouteBreadcrumbs(nuxtApp, config);

  nuxtApp.hook('page:finish', (page) => {
    if (!page?.path) {
      return;
    }

    setRouteScopeMetadata(page.path);
  });

  const provideConsoleBreadcrumb = parseBoolean(runtimeConfig.public?.sentry?.consoleBreadcrumbs ?? true);
  if (provideConsoleBreadcrumb) {
    nuxtApp.hook('vue:error', (error, instance, info) => {
      Sentry.addBreadcrumb({
        level: 'error',
        category: 'vue',
        message: info ?? error.message,
        data: {
          component: instance?.$options?.name,
          info,
        },
      });
    });
  }

  if (sendTestError) {
    const emitTestError = (): void => {
      Sentry.withScope((scope) => {
        scope.setTag('sentry_test_event', 'true');
        scope.setContext('test_event', { triggeredAt: new Date().toISOString() });
        Sentry.captureException(
          new Error('Sentry test error triggered via runtimeConfig.public.sentry.sendTestError'),
        );
      });

      if (config.debug) {
        console.info('[Sentry] Test error captured because runtimeConfig.public.sentry.sendTestError=true');
      }
    };

    nuxtApp.hook('app:mounted', () => {
      const usersStore = useUsersStore();
      const { authUserData } = storeToRefs(usersStore);

      if (authUserData.value) {
        emitTestError();
        return;
      }

      let stopWatch: WatchStopHandle | null = null;
      let timeout: ReturnType<typeof setTimeout> | null = null;

      const cleanup = (): void => {
        if (timeout !== null) {
          clearTimeout(timeout);
          timeout = null;
        }

        if (stopWatch) {
          stopWatch();
          stopWatch = null;
        }
      };

      stopWatch = watch(authUserData, (user) => {
        if (!user) {
          return;
        }

        cleanup();
        emitTestError();
      });

      timeout = setTimeout(() => {
        cleanup();
        emitTestError();
      }, 5000);

      nuxtApp.hook('app:unmounted', cleanup);
    });
  }

  const captureMessageWithContext = (message: string, level?: SeverityLevel, context?: Record<string, unknown>): string => {
    if (typeof level !== 'undefined' || typeof context !== 'undefined') {
      const captureContext: CaptureContext = {};

      if (typeof level !== 'undefined') {
        captureContext.level = level;
      }

      if (typeof context !== 'undefined') {
        captureContext.extra = context;
      }

      return Sentry.captureMessage(message, captureContext);
    }

    return Sentry.captureMessage(message);
  };

  return {
    provide: {
      sentrySdk: Sentry,
      sentryScope (callback: (scope: Scope) => void): void {
        Sentry.withScope(callback);
      },
      sentrySetContext: Sentry.setContext,
      sentrySetUser: Sentry.setUser,
      sentrySetTag: Sentry.setTag,
      sentryAddBreadcrumb: Sentry.addBreadcrumb,
      sentryCaptureException: Sentry.captureException,
      sentryCaptureMessage: captureMessageWithContext,
    },
  };
});
